// V4-FRONT-ANY-USAGE 2026-05-28
// Petit helper pour extraire un message d'erreur d'un payload d'erreur axios sans
// recourir à `error: any` dans tous les hooks. Le shape attendu côté backend FastAPI :
//   - 4xx / 5xx → { detail: string | [{ msg, ... }] }
//   - error envelopes maison → { error: { message: string, code?: string } }
// Si la forme est inconnue, on retourne un fallback fourni par le caller (i18n).

import axios from 'axios';

interface FastApiErrorBody {
  // `detail` peut être une string, une liste de validation, OU un objet structuré
  // maison `{ error, message }` (cf convention error-surface, ~/.claude/CLAUDE.md).
  detail?: unknown;
  // `error` peut être un objet enveloppe ({ message }) OU une string brute
  // (certains backends maison, ex. Zernio connect, renvoient `error: "..."`).
  error?: { message?: unknown } | string;
}

/**
 * Extrait le message lisible d'une erreur HTTP. Tolère les payloads non-axios
 * (ex. erreurs JS pures) et renvoie `fallback` si rien d'utilisable n'est trouvé.
 */
export function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as FastApiErrorBody | undefined;
    const bodyError = body?.error;
    if (typeof bodyError === 'string' && bodyError) return bodyError;
    const envelopeMessage = typeof bodyError === 'object' ? bodyError?.message : undefined;
    if (typeof envelopeMessage === 'string' && envelopeMessage) return envelopeMessage;
    const detail = body?.detail;
    if (typeof detail === 'string' && detail) return detail;
    // detail structuré maison `{ error, message }` (ex. 409 email_already_registered)
    if (detail && typeof detail === 'object' && 'message' in detail) {
      const detailMessage = (detail as { message?: unknown }).message;
      if (typeof detailMessage === 'string' && detailMessage) return detailMessage;
    }
    if (error.message) return error.message;
    return fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/**
 * Renvoie le code HTTP d'une erreur axios, ou `undefined` si l'erreur n'est pas
 * une AxiosError (ex. erreur JS pure). Évite le `error: any` juste pour lire
 * `error.response.status` (typiquement le check 429 rate-limit).
 */
export function getApiErrorStatus(error: unknown): number | undefined {
  if (axios.isAxiosError(error)) return error.response?.status;
  return undefined;
}

/**
 * Renvoie la valeur brute de `response.data.detail` (string) si présente.
 * Utile quand le backend renvoie un *code* machine dans `detail`
 * (ex. `email_not_verified`) qu'il faut comparer, pas juste afficher.
 */
export function getApiErrorDetail(error: unknown): string | undefined {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as FastApiErrorBody | undefined)?.detail;
    if (typeof detail === 'string') return detail;
  }
  return undefined;
}

/**
 * Renvoie le *code* machine d'une erreur, qu'il soit dans `detail` (string brute,
 * ex. `email_not_verified`) OU dans `detail.error` (objet structuré error-surface,
 * ex. `account_inactive`, `read_only_account`). À utiliser pour brancher sur un code
 * stable plutôt que sur le message localisé.
 */
export function getApiErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const detail = (error.response?.data as FastApiErrorBody | undefined)?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object' && 'error' in detail) {
    const code = (detail as { error?: unknown }).error;
    if (typeof code === 'string') return code;
  }
  return undefined;
}
